import { ContentHeader } from 'components/ui/layout/ContentHeader';
import { useTranslation } from 'react-i18next';
import { DarwinListView } from 'components/darwin-ui';
import { getRolesColumns } from './columns';
import { PermissionActions, PermissionModules, Role } from 'types/rolesTypes';
import { useGetRoles } from 'api/services/rolesService';
import useTableControls from 'hooks/useTableControls';
import { generatePath, useNavigate } from 'react-router-dom';

import icons from 'components/darwin-ui/Icon/Icons';
import { ROUTES } from 'routes/routes';
import { useEffect, useState } from 'react';
import { useFloatNotification } from 'contexts/FloatNotificationContext';
import { usePermissionChecker } from 'hooks/usePermissionChecker';
import { DeleteRoleModal } from 'components/ui/roles/DeleteRoleModal';
import { useUI } from 'contexts/UiContext';

export type ColumnAction = {
  key: string;
  name: keyof typeof icons;
  label: string;
  onClick?: (role: Role) => void;
};

export const Roles = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { openNotification } = useFloatNotification();
  const [roleToDelete, setRoleToDelete] = useState<Role>();

  const { selectedCompany } = useUI();

  const checkPermission = usePermissionChecker();

  const viewRolesPermission = checkPermission(
    PermissionModules.RoleManagement,
    PermissionActions.Read
  );
  const updateRolePermission = checkPermission(
    PermissionModules.RoleManagement,
    PermissionActions.Update
  );
  const deleteRolePermission = checkPermission(
    PermissionModules.RoleManagement,
    PermissionActions.Delete
  );
  const createRolePermission = checkPermission(
    PermissionModules.RoleManagement,
    PermissionActions.Create
  );

  const {
    pageSize,
    currentPage,
    onPageSizeChange,
    setCurrentPage,
    searchQuery,
    setSearchQuery,
    sortOrder,
    onTableChange,
  } = useTableControls();

  const {
    data: rolesData,
    isLoading: isLoadingRoles,
    isFetching: isFetchingRoles,
    isPending: isPendingRoles,
    error: rolesError,
  } = useGetRoles({
    companyId: selectedCompany || '',
    page: currentPage,
    limit: pageSize,
    query: searchQuery,
    sortOrder,
  });

  useEffect(() => {
    if (rolesError?.message) {
      openNotification('Error', t('roles.roles-list.loading-error'), 'error');
    }
  }, [openNotification, rolesError?.message, t]);

  const isLoading =
    isLoadingRoles ||
    isFetchingRoles ||
    isPendingRoles ||
    viewRolesPermission === null;

  const tableData = rolesData?.data ?? [];

  const availableActions: Array<{
    permission: boolean | null;
    action: ColumnAction;
  }> = [
    {
      permission: updateRolePermission,
      action: {
        key: 'edit',
        name: 'edit',
        label: t('list-view.action-items.edit'),
        onClick: role =>
          navigate(generatePath(ROUTES.ROLES.EDIT, { id: role.id.toString() })),
      },
    },
    {
      permission: viewRolesPermission,
      action: {
        key: 'view',
        name: 'view',
        label: t('list-view.action-items.view'),
        onClick: role =>
          navigate(generatePath(ROUTES.ROLES.VIEW, { id: role.id.toString() })),
      },
    },
    {
      permission: deleteRolePermission,
      action: {
        key: 'delete',
        name: 'delete',
        label: t('list-view.action-items.delete'),
        onClick: setRoleToDelete,
      },
    },
    {
      permission: createRolePermission,
      action: {
        key: 'duplicate',
        name: 'duplicate',
        label: t('list-view.action-items.duplicate'),
        onClick: role => {
          navigate(
            generatePath(ROUTES.ROLES.DUPLICATE, { id: role.id.toString() })
          );
        },
      },
    },
  ];

  const actions: ColumnAction[] = availableActions
    .filter(item => Boolean(item.permission))
    .map(item => item.action);

  const columns = getRolesColumns(t, actions, searchQuery);

  return (
    <>
      <ContentHeader title={t('roles.roles-list.title')} />

      <DarwinListView<Role>
        data={tableData}
        columns={columns}
        isLoading={isLoading}
        header={{
          setSearchQuery,
          searchPlaceholder: t('roles.roles-list.search-placeholder'),
        }}
        table={{ onChange: onTableChange }}
        pagination={{
          total: rolesData?.metadata?.total,
          pageSize,
          currentPage,
          onPageChange: setCurrentPage,
          onPageSizeChange,
        }}
      />

      <DeleteRoleModal
        open={!!roleToDelete}
        onCancel={() => setRoleToDelete(undefined)}
        roleId={roleToDelete?.id.toString()}
      />
    </>
  );
};
